import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, XCircle, Download } from 'lucide-react';
import { getBankCrosscheck, downloadCsv, inr } from '../data';
import type { BankCrosscheck as BankCrosscheckT } from '../types';

const STATUS_LABEL: Record<string, string> = {
	confirmed: 'Matched',
	partial: 'Partial',
	none: 'Not matched',
};

type Tab = 'confirmed' | 'partial' | 'none';

/** "Do we have a bank debit to each vendor?" — Matched vendors up front, gaps in their own tabs. */
export const BankCrosscheck: React.FC = () => {
	const [d, setD] = useState<BankCrosscheckT | null>(null);
	const [err, setErr] = useState<string | null>(null);
	const [tab, setTab] = useState<Tab>('confirmed');

	useEffect(() => {
		let on = true;
		getBankCrosscheck()
			.then(x => on && setD(x))
			.catch(e => on && setErr(e?.message || 'Failed'));
		return () => {
			on = false;
		};
	}, []);

	const rows = useMemo(() => (d ? d.vendors.filter(v => v.status === tab) : []), [d, tab]);

	if (err) return <div className='p-6 text-red-600'>Error: {err}</div>;
	if (!d)
		return (
			<div className='flex items-center gap-2 p-6 text-gray-500'>
				<Loader2 className='animate-spin' size={18} /> Loading vendor bank cross-check…
			</div>
		);

	const s = d.summary;
	const isGap = tab !== 'confirmed';

	const exportCsv = () => {
		downloadCsv(
			'vendor-bank-crosscheck.csv',
			[
				'Vendor',
				'Status',
				'Payments',
				'Confirmed payments',
				'Zoho paid (INR)',
				'Bank confirmed (INR)',
				'Gap (INR)',
				'Why unconfirmed',
			],
			d.vendors.map(v => [
				v.vendor,
				STATUS_LABEL[v.status] ?? v.status,
				v.payments,
				v.confirmed_payments,
				v.zoho_paid,
				v.confirmed_value,
				v.gap_value,
				v.reason ?? '',
			])
		);
	};

	return (
		<section className='bg-white rounded-lg border border-gray-200 p-5'>
			<div className='flex items-start justify-between gap-3 mb-1'>
				<h3 className='font-semibold text-gray-900'>
					Vendor bank cross-check — vendors matched to a bank debit
				</h3>
				<button
					onClick={exportCsv}
					className='flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 whitespace-nowrap'>
					<Download size={15} />
					Download CSV ({s.vendors})
				</button>
			</div>
			<p className='text-xs text-gray-400 mb-4'>{d.note}</p>

			<div className='grid grid-cols-2 md:grid-cols-4 gap-3 mb-5'>
				<Tile
					icon={<CheckCircle2 size={16} className='text-green-600' />}
					label='Fully matched'
					value={s.fully_confirmed}
					sub={`of ${s.vendors} vendors`}
				/>
				<Tile
					icon={<AlertTriangle size={16} className='text-amber-600' />}
					label='Partially'
					value={s.partially_confirmed}
				/>
				<Tile
					icon={<XCircle size={16} className='text-red-600' />}
					label='Not matched'
					value={s.not_confirmed}
				/>
				<Tile
					icon={<span className='text-rose-600 text-sm font-bold'>₹</span>}
					label='Unconfirmed value'
					value={inr(s.unconfirmed_value)}
					isText
				/>
			</div>

			<div className='flex gap-1 mb-3 border-b border-gray-200'>
				<TabBtn active={tab === 'confirmed'} onClick={() => setTab('confirmed')}>
					Matched ({s.fully_confirmed})
				</TabBtn>
				<TabBtn active={tab === 'partial'} onClick={() => setTab('partial')}>
					Partial ({s.partially_confirmed})
				</TabBtn>
				<TabBtn active={tab === 'none'} onClick={() => setTab('none')}>
					Not matched ({s.not_confirmed})
				</TabBtn>
			</div>

			<table className='w-full text-sm'>
				<thead className='text-gray-500 text-xs uppercase'>
					<tr>
						<th className='text-left py-2'>Vendor</th>
						<th className='text-right py-2'>Zoho paid</th>
						<th className='text-right py-2'>Bank confirmed</th>
						<th className='text-right py-2'>Payments</th>
						{isGap && <th className='text-right py-2'>Gap</th>}
						{isGap && <th className='text-left py-2 pl-4'>Why unconfirmed</th>}
					</tr>
				</thead>
				<tbody>
					{rows.map(v => (
						<tr key={v.vendor} className='border-t border-gray-100'>
							<td className='py-2 text-gray-800'>{v.vendor}</td>
							<td className='py-2 text-right text-gray-600'>{inr(v.zoho_paid)}</td>
							<td className='py-2 text-right text-green-700'>{inr(v.confirmed_value)}</td>
							<td className='py-2 text-right text-gray-500'>
								{v.confirmed_payments}/{v.payments}
							</td>
							{isGap && (
								<td className='py-2 text-right font-medium text-rose-600'>{inr(v.gap_value)}</td>
							)}
							{isGap && <td className='py-2 pl-4 text-xs text-gray-500'>{v.reason}</td>}
						</tr>
					))}
					{rows.length === 0 && (
						<tr>
							<td colSpan={6} className='py-6 text-center text-gray-400 text-sm'>
								No vendors in this bucket.
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</section>
	);
};

const TabBtn: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
	active,
	onClick,
	children,
}) => (
	<button
		onClick={onClick}
		className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 ${
			active
				? 'border-blue-600 text-blue-700'
				: 'border-transparent text-gray-500 hover:text-gray-700'
		}`}>
		{children}
	</button>
);

const Tile: React.FC<{
	icon: React.ReactNode;
	label: string;
	value: number | string;
	sub?: string;
	isText?: boolean;
}> = ({ icon, label, value, sub, isText }) => (
	<div className='p-3 rounded-lg bg-gray-50 border border-gray-100'>
		<div className='flex items-center gap-1.5 text-xs text-gray-500 mb-1'>
			{icon}
			{label}
		</div>
		<div className={`font-bold text-gray-900 ${isText ? 'text-lg' : 'text-2xl'}`}>
			{typeof value === 'number' ? value.toLocaleString() : value}
		</div>
		{sub && <div className='text-xs text-gray-400'>{sub}</div>}
	</div>
);
